import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { IconArrowLeft, IconPlus } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Switch } from "../../../../components/common/switch/switch";
import { SectionCard } from "../../../../components/common/section-card";
import { RequiredMark } from "../../../../components/common/required-mark";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { LOCAL_STORAGE_KEYS } from "../../../../utils/localStorageKeys";
import { ImageDropzone } from "../components/image-dropzone";
import { WarrantyCard, type Warranty } from "../components/warranty-card";
import { BasicInfoSection } from "../components/basic-info-section";
import {
  VariantsBuilderSection,
  type ResolvedVariant,
} from "../components/variants-builder-section";
import type { VariantPricingTierEdit } from "../components/variant-pricing-row";
import { CompatibleWarrantiesSection } from "../components/compatible-warranties-section";
import type { VariantOption, VariantOptionAxis } from "../components/option-card";
import type { VariantRowEdit } from "../components/variant-pricing-row";
import {
  AXIS_DTO_FIELD,
  VARIANT_AXES,
  type NonEmptyVariantAxis,
} from "../components/variant-axis-registry";
import { useDebugMyProductAutofill } from "../../hooks/useDebugMyProductAutofill";
import { useProductRequestSubmissionEligibility } from "../../../product-requests/hooks/useProductRequestSubmissionEligibility";
import {
  productRequestsService,
  updateProductRequest,
  deleteProductRequest,
  productDatasheetsService,
} from "../../../api_client";
import type {
  CreateProductRequestDto,
  CreateProductVariantDto,
  ProductRequestDto,
} from "../../../../api";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import {
  uploadProductImage,
  ProductImageUploadError,
} from "../../utils/upload-product-image";
import { DatasheetUpload } from "../components/datasheet-upload";
import { DatasheetExtractionDialog, type AutofillPatch } from "../components/datasheet-extraction-dialog";
import { parseDatasheetSummary } from "../../utils/datasheet-extraction-schema";
import { useBrands } from "../../hooks/useBrands";
import { useCategories } from "../../hooks/useCategories";
import { useCategoryFieldConfig } from "../../hooks/useCategoryFieldConfig";
import { useProductRequestDetails } from "../../../product-requests/hooks/useProductRequestDetails";
import { useMePartner } from "../../../../swr";

const parseIntOrUndef = (s: string): number | undefined => {
  if (!s) return undefined;
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? undefined : n;
};

const parseFloatOrUndef = (s: string): number | undefined => {
  if (!s) return undefined;
  const n = parseFloat(s);
  return Number.isNaN(n) ? undefined : n;
};

function omit<T, K extends keyof T>(obj: T, ...keys: K[]): Omit<T, K> {
  const result = { ...obj };
  for (const k of keys) delete result[k];
  return result;
}

function isNonEmptyString(value: string | undefined | null): boolean {
  return Boolean(value?.trim());
}

function validateNumberField(label: string, value: string): string | undefined {
  if (!isNonEmptyString(value)) return `${label} is required`;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return `${label} must be a valid non-negative number`;
  return undefined;
}

const PRODUCT_DRAFT_STORAGE_PREFIX = "my-product-new-draft";
const DEFAULT_WARRANTIES: Warranty[] = [
  { id: 1, warrantyId: "", warrantyTypeId: "", warrantyProviderId: "" },
];
const DEFAULT_VARIANT_PRICING_TIERS: VariantPricingTierEdit[] = [{ id: 1, minQty: "1", price: "" }];
const DEFAULT_OPTIONS: VariantOption[] = [{ id: 1, name: "", valueIds: [] }];

type StoredDraftFile = {
  name: string;
  type: string;
  dataUrl: string;
};

type StoredVariantEdit = Omit<VariantRowEdit, "imageFile" | "imagePreviewUrl"> & {
  imageFile?: StoredDraftFile;
  imagePreviewUrl?: string;
};

/**
 * Local autosave of a brand-new product that hasn't been saved to the server
 * yet. Once it is (Save as Draft), the server copy is the source of truth and
 * is edited from its own route.
 */
type MyProductDraft = {
  version: 2;
  /** Written by older builds for server-saved drafts; such entries are discarded. */
  requestId?: string;
  images: StoredDraftFile[];
  name: string;
  brandId: string;
  categoryId: string;
  sku: string;
  countryOfOriginId: string;
  dealTypeId: string;
  screenSizeId: string;
  resolutionId: string;
  datasheetUrl: string;
  datasheetObjectKey?: string | null;
  datasheetFileName?: string | null;
  datasheetExtractionId?: string | null;
  notes: string;
  description: string;
  warranties: Warranty[];
  variantsEnabled: boolean;
  options: VariantOption[];
  variantEdits: Record<string, StoredVariantEdit>;
  removedVariantKeys: string[];
  visibility: boolean;
};

/** Legacy draft shape (version 1) — used only for migration. */
type MyProductDraftV1 = Omit<MyProductDraft, "version"> & {
  version: 1;
  pricingTiers?: Array<{ id: number; minQty: string; price: string }>;
};

function getMyProductDraftStorageKey() {
  if (typeof window === "undefined") return PRODUCT_DRAFT_STORAGE_PREFIX;
  const userId = localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_USER_ID) ?? "guest";
  return `${PRODUCT_DRAFT_STORAGE_PREFIX}:${userId}`;
}

function isNonEmptyDraft(draft: Partial<MyProductDraft>) {
  return Boolean(
    draft.name?.trim() ||
      draft.brandId ||
      draft.categoryId ||
      draft.sku ||
      draft.countryOfOriginId ||
      draft.dealTypeId ||
      draft.screenSizeId ||
      draft.resolutionId ||
      draft.datasheetUrl ||
      draft.notes ||
      draft.description ||
      (draft.warranties?.length ?? 0) > 0 ||
      (draft.images?.length ?? 0) > 0 ||
      (draft.options?.length ?? 0) > 0 ||
      Object.keys(draft.variantEdits ?? {}).length > 0 ||
      (draft.removedVariantKeys?.length ?? 0) > 0,
  );
}

function serializeFile(file: File): Promise<StoredDraftFile> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.onload = () => {
      resolve({
        name: file.name,
        type: file.type,
        dataUrl: String(reader.result),
      });
    };
    reader.readAsDataURL(file);
  });
}

async function deserializeFile(file: StoredDraftFile): Promise<File> {
  const response = await fetch(file.dataUrl);
  const blob = await response.blob();
  return new File([blob], file.name || "image", { type: file.type || blob.type || "application/octet-stream" });
}

function requestToWarranties(request: ProductRequestDto): Warranty[] {
  // warrantyProviderId is exposed by the backend response but won't appear on
  // the generated client until `npm run generate:api` runs after the BE
  // schema/DTO change. The cast disappears after regen.
  const r = request as ProductRequestDto & { warrantyProviderId?: number | null };
  return [
    {
      id: 1,
      warrantyId: r.warrantyId != null ? String(r.warrantyId) : "",
      warrantyTypeId: r.warrantyTypeId != null ? String(r.warrantyTypeId) : "",
      warrantyProviderId: r.warrantyProviderId != null ? String(r.warrantyProviderId) : "",
    },
  ];
}

function requestToVariantBuilderState(request: ProductRequestDto): {
  options: VariantOption[];
  variantEdits: Record<string, VariantRowEdit>;
} {
  const idsByAxis: Record<NonEmptyVariantAxis, Set<string>> = {
    RAM: new Set(),
    Storage: new Set(),
    Color: new Set(),
    CPU: new Set(),
  };

  type RequestVariant = ProductRequestDto["variants"][number];

  function axisId(v: RequestVariant, axis: NonEmptyVariantAxis): number | null | undefined {
    return (v as unknown as Record<string, number | null | undefined>)[
      AXIS_DTO_FIELD[axis] as string
    ];
  }

  for (const v of request.variants ?? []) {
    for (const axis of VARIANT_AXES) {
      const id = axisId(v, axis);
      if (id != null) idsByAxis[axis].add(String(id));
    }
  }

  const options: VariantOption[] = [];
  let nextId = 1;
  for (const axis of VARIANT_AXES) {
    if (idsByAxis[axis].size > 0) {
      options.push({ id: nextId++, name: axis, valueIds: Array.from(idsByAxis[axis]) });
    }
  }

  const variantEdits: Record<string, VariantRowEdit> = {};
  for (const v of request.variants ?? []) {
    const parts: string[] = [];
    for (const axis of VARIANT_AXES) {
      const id = axisId(v, axis);
      if (id != null) parts.push(`${axis}:${id}`);
    }
    const key = parts.join("|");
    if (!key) continue;
    variantEdits[key] = {
      sku: v.sku ?? "",
      stock: v.quantity != null ? String(v.quantity) : "",
      conditionId: v.conditionId != null ? String(v.conditionId) : "",
      model: v.model ?? "",
      imagePreviewUrl: v.image ?? undefined,
      pricingTiers:
        (v.pricingTiers ?? []).length > 0
          ? [...v.pricingTiers]
              .sort((a, b) => a.minQty - b.minQty)
              .map((tier, index) => ({
                id: index + 1,
                minQty: String(tier.minQty),
                price: String(tier.price),
              }))
          : [{ id: 1, minQty: "1", price: "" }],
    };
  }

  return { options, variantEdits };
}

interface BulkPreviewState {
  bulkPreviewDto?: CreateProductRequestDto;
  bulkPreviewIndex?: number;
}

export function MyProductNewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSeller } = useMePartner();
  const { id: requestIdParam } = useParams<{ id?: string }>();
  const requestId = requestIdParam?.trim() || undefined;
  const locationState = location.state as (BulkPreviewState & { fromBulkUpload?: boolean }) | null;
  const bulkPreview = locationState?.bulkPreviewDto;
  const bulkPreviewIndex = locationState?.bulkPreviewIndex;
  const fromBulkUpload = locationState?.fromBulkUpload ?? false;
  const isBulkPreview = bulkPreview != null;
  const {
    data: requestDetails,
    error: requestDetailsError,
  } = useProductRequestDetails(requestId);
  const draftStorageKey = useMemo(() => getMyProductDraftStorageKey(), []);
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [hasSavedDraft, setHasSavedDraft] = useState(false);
  const [savedRequestId, setSavedRequestId] = useState<string | undefined>(requestId);
  const suppressDraftPersistenceRef = useRef(false);
  const hydratedRequestRef = useRef(false);

  const [images, setImages] = useState<string[]>([]);
  const [templateImageFiles, setTemplateImageFiles] = useState<File[]>([]);
  const [templateUploadProgress, setTemplateUploadProgress] = useState<number | undefined>();
  const [name, setName] = useState("");
  const [brandId, setBrandId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [sku, setSku] = useState("");
  const [countryOfOriginId, setCountryOfOriginId] = useState("");
  const [dealTypeId, setDealTypeId] = useState("");
  const [screenSizeId, setScreenSizeId] = useState("");
  const [resolutionId, setResolutionId] = useState("");
  const [datasheetUrl, setDatasheetUrl] = useState("");
  // PDF-upload path (single-product flow). The object key + file name come from
  // the GCS signed-URL flow; the extraction id comes from POST /product-datasheets/extract.
  // The summary returned alongside the id is parsed and held only for the autofill dialog —
  // not stored in the draft (we just keep the id, which is the durable handle).
  const [datasheetObjectKey, setDatasheetObjectKey] = useState<string | null>(null);
  const [datasheetFileName, setDatasheetFileName] = useState<string | null>(null);
  const [datasheetExtractionId, setDatasheetExtractionId] = useState<string | null>(null);
  const [datasheetSummaryForDialog, setDatasheetSummaryForDialog] = useState<string | null>(null);
  const [extractionDialogOpen, setExtractionDialogOpen] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [notes, setNotes] = useState("");
  const [description, setDescription] = useState("");
  const [warranties, setWarranties] = useState<Warranty[]>(DEFAULT_WARRANTIES);
  const [compatibleWarrantyIds, setCompatibleWarrantyIds] = useState<number[]>([]);
  const [variantsEnabled, setVariantsEnabled] = useState(true);
  const [options, setOptions] = useState<VariantOption[]>(DEFAULT_OPTIONS);
  const [variantEdits, setVariantEdits] = useState<Record<string, VariantRowEdit>>({});
  const [removedVariantKeys, setRemovedVariantKeys] = useState<Set<string>>(new Set());
  const [visibility, setVisibility] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());

  // Per-category field policy: drives which spec fields/axes are shown and which
  // are required. Permissive default (all visible, none required) until a
  // category is picked, so the form is unchanged before then.
  const { config: fieldPolicy } = useCategoryFieldConfig(parseIntOrUndef(categoryId));
  // Mirrors the warranty checks in validateSubmitForm().
  const warrantyRequirements = {
    period: fieldPolicy.warranty.required,
    type: fieldPolicy.warrantyType.visible && fieldPolicy.warrantyType.required,
    provider: fieldPolicy.warrantyProvider.visible && fieldPolicy.warrantyProvider.required,
  };

  // Brand + category options powering the autofill dialog's "did we find a
  // matching brand?" hint. SWR cache is shared with BasicInfoSection.
  const { data: brandsForDialog } = useBrands();
  const { data: categoriesForDialog } = useCategories();
  const brandOptionsForDialog = useMemo(
    () => (brandsForDialog ?? []).map((b) => ({ value: String(b.id), label: b.name })),
    [brandsForDialog],
  );
  const categoryOptionsForDialog = useMemo(
    () =>
      (categoriesForDialog ?? []).map((c) => ({
        value: String(c.id),
        label: c.completeName ?? c.displayName ?? c.name,
      })),
    [categoriesForDialog],
  );

  const parsedDatasheetSummary = useMemo(
    () => parseDatasheetSummary(datasheetSummaryForDialog),
    [datasheetSummaryForDialog],
  );

  const applyDatasheetAutofill = useCallback((patch: AutofillPatch) => {
    if (patch.name != null) setName(patch.name);
    if (patch.description != null) setDescription(patch.description);
    if (patch.sku != null) setSku(patch.sku);
    if (patch.brandId != null) setBrandId(patch.brandId);
    if (patch.appendedNotes) {
      setNotes((prev) => (prev?.trim() ? `${prev.trim()}\n\n${patch.appendedNotes}` : patch.appendedNotes!));
    }
    setExtractionDialogOpen(false);
    showSuccessToast("Datasheet details applied. Review and submit when ready.");
  }, []);

  const handleDatasheetUploaded = useCallback(
    (objectKey: string, fileName: string) => {
      setDatasheetObjectKey(objectKey);
      setDatasheetFileName(fileName);
      // PDF is the source of truth — clear any legacy external URL.
      setDatasheetUrl("");
      // Trigger sync extraction. Failure is soft: the seller can still submit,
      // and BE's bulk fallback will retry server-side.
      void (async () => {
        setExtracting(true);
        try {
          const res = await productDatasheetsService.extract({ objectKey, fileName });
          setDatasheetExtractionId(res.data.extractionId);
          if (res.data.status === "success" && res.data.aiSummary) {
            setDatasheetSummaryForDialog(res.data.aiSummary);
            setExtractionDialogOpen(true);
          } else {
            showErrorToast(
              "Datasheet uploaded, but AI extraction returned no autofill data. You can still submit — the catalog AI will retry in the background.",
            );
          }
        } catch {
          showErrorToast(
            "Datasheet uploaded, but AI extraction failed. You can still submit — the catalog AI will retry in the background.",
          );
        } finally {
          setExtracting(false);
        }
      })();
    },
    [],
  );

  const handleDatasheetRemove = useCallback(() => {
    setDatasheetObjectKey(null);
    setDatasheetFileName(null);
    setDatasheetExtractionId(null);
    setDatasheetSummaryForDialog(null);
  }, []);

  const { isDebug, handleDevAutofill } = useDebugMyProductAutofill({
    setImages,
    setTemplateImageFiles,
    setName,
    setBrandId,
    setCategoryId,
    setSku,
    setCountryOfOriginId,
    setDealTypeId,
    setScreenSizeId,
    setResolutionId,
    setDatasheetUrl,
    setNotes,
    setDescription,
    setWarranties,
    setVariantsEnabled,
    setOptions,
    setVariantEdits,
    setRemovedVariantKeys,
    setVisibility,
  });
  const {
    canSubmit: canSubmitProductRequest,
    reason: productRequestBlockReason,
  } = useProductRequestSubmissionEligibility();

  // Labels resolved by the option cards so we can display variant rows even
  // though we store only IDs in option.valueIds.
  const [labelsByAxis, setLabelsByAxis] = useState<Partial<Record<VariantOptionAxis, Record<string, string>>>>({});
  const handleLabelsResolved = useCallback(
    (axis: VariantOptionAxis, labels: Record<string, string>) => {
      setLabelsByAxis((prev) => {
        const existing = prev[axis] ?? {};
        // Skip update when the maps are identical to avoid an effect→state loop.
        const sameSize = Object.keys(existing).length === Object.keys(labels).length;
        const sameEntries = sameSize && Object.entries(labels).every(([k, v]) => existing[k] === v);
        if (sameEntries) return prev;
        return { ...prev, [axis]: { ...existing, ...labels } };
      });
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;

    async function hydrateDraft() {
      if (bulkPreview) return;
      if (requestId) return;

      if (typeof window === "undefined") {
        setDraftHydrated(true);
        return;
      }

      const raw = localStorage.getItem(draftStorageKey);
      if (!raw) {
        if (!requestId) {
          setDraftHydrated(true);
          setHasSavedDraft(false);
        }
        return;
      }

      try {
        const parsed = JSON.parse(raw) as Partial<MyProductDraft | MyProductDraftV1>;
        if (!parsed || (parsed.version !== 2 && parsed.version !== 1)) {
          if (!requestId) {
            setDraftHydrated(true);
            setHasSavedDraft(false);
          }
          return;
        }

        // Migrate v1 → v2: copy top-level pricingTiers into each variant edit
        let draft: Partial<MyProductDraft>;
        if (parsed.version === 1) {
          const v1 = parsed as Partial<MyProductDraftV1>;
          const legacyTiers = v1.pricingTiers ?? DEFAULT_VARIANT_PRICING_TIERS;
          const migratedEdits: Record<string, StoredVariantEdit> = {};
          for (const [key, edit] of Object.entries(v1.variantEdits ?? {})) {
            migratedEdits[key] = {
              ...edit,
              pricingTiers: edit.pricingTiers ?? legacyTiers,
            };
          }
          draft = { ...omit(v1, "pricingTiers"), version: 2, variantEdits: migratedEdits } as Partial<MyProductDraft>;
        } else {
          draft = parsed as Partial<MyProductDraft>;
        }

        if (draft.requestId) {
          // Already saved on the server (it's in Product Requests) — "Add a new
          // product" must not reopen it.
          localStorage.removeItem(draftStorageKey);
          setHasSavedDraft(false);
          return;
        }

        hydratedRequestRef.current = true;
        setHasSavedDraft(isNonEmptyDraft(draft));

        setImages([]);
        setTemplateImageFiles([]);

        setName(draft.name ?? "");
        setBrandId(draft.brandId ?? "");
        setCategoryId(draft.categoryId ?? "");
        setSku(draft.sku ?? "");
        setCountryOfOriginId(draft.countryOfOriginId ?? "");
        setDealTypeId(draft.dealTypeId ?? "");
        setScreenSizeId(draft.screenSizeId ?? "");
        setResolutionId(draft.resolutionId ?? "");
        setDatasheetUrl(draft.datasheetUrl ?? "");
        setDatasheetObjectKey(draft.datasheetObjectKey ?? null);
        setDatasheetFileName(draft.datasheetFileName ?? null);
        setDatasheetExtractionId(draft.datasheetExtractionId ?? null);
        setNotes(draft.notes ?? "");
        setDescription(draft.description ?? "");
        setWarranties(draft.warranties ?? DEFAULT_WARRANTIES);
        setVariantsEnabled(draft.variantsEnabled ?? true);
        setOptions(draft.options ?? DEFAULT_OPTIONS);
        setRemovedVariantKeys(new Set(draft.removedVariantKeys ?? []));
        setVisibility(draft.visibility ?? true);

        const restoredImages = await Promise.all((draft.images ?? []).map(async (file) => {
          const restoredFile = await deserializeFile(file);
          return {
            file: restoredFile,
            dataUrl: file.dataUrl,
          };
        }));
        if (cancelled) return;
        setImages(restoredImages.map((item) => item.dataUrl));
        setTemplateImageFiles(restoredImages.map((item) => item.file));

        const restoredVariantEdits = await Promise.all(
          Object.entries(draft.variantEdits ?? {}).map(async ([key, edit]) => {
            if (!edit.imageFile) {
              return [key, omit(edit, "imagePreviewUrl")] as const;
            }

            const restoredFile = await deserializeFile(edit.imageFile);
            return [
              key,
              {
                ...omit(edit, "imagePreviewUrl"),
                imageFile: restoredFile,
                imagePreviewUrl: edit.imagePreviewUrl ?? edit.imageFile.dataUrl,
              },
            ] as const;
          }),
        );
        if (cancelled) return;
        setVariantEdits(Object.fromEntries(restoredVariantEdits));
      } catch {
        // Ignore malformed drafts and start fresh.
        if (!requestId) {
          setDraftHydrated(true);
          setHasSavedDraft(false);
        }
      } finally {
        if (!cancelled && (!requestId || hydratedRequestRef.current)) {
          setDraftHydrated(true);
        }
      }
    }

    void hydrateDraft();

    return () => {
      cancelled = true;
    };
  }, [draftStorageKey, bulkPreview, requestId]);

  useEffect(() => {
    if (!requestId || draftHydrated || hydratedRequestRef.current) return;
    if (!requestDetails) return;

    hydratedRequestRef.current = true;
    suppressDraftPersistenceRef.current = true;

    setSavedRequestId(requestDetails.id);
    setHasSavedDraft(false);
    const mainImg = requestDetails.image;
    const gallery = requestDetails.galleryImages ?? [];
    setImages([...(mainImg ? [mainImg] : []), ...gallery]);
    setTemplateImageFiles([]);
    setName(requestDetails.name ?? "");
    setBrandId(requestDetails.brandId != null ? String(requestDetails.brandId) : "");
    setCategoryId(requestDetails.categoryId != null ? String(requestDetails.categoryId) : "");
    setSku(requestDetails.variants?.[0]?.sku ?? "");
    setCountryOfOriginId(
      requestDetails.countryOfOriginId != null ? String(requestDetails.countryOfOriginId) : "",
    );
    setDealTypeId(requestDetails.dealTypeId != null ? String(requestDetails.dealTypeId) : "");
    setScreenSizeId(
      requestDetails.screenSizeId != null ? String(requestDetails.screenSizeId) : "",
    );
    setResolutionId(
      requestDetails.resolutionId != null ? String(requestDetails.resolutionId) : "",
    );
    setDatasheetUrl(requestDetails.datasheetUrl ?? "");
    setDatasheetObjectKey(requestDetails.datasheetObjectKey ?? null);
    setDatasheetFileName(requestDetails.datasheetFileName ?? null);
    setDatasheetExtractionId(requestDetails.datasheetExtractionId ?? null);
    setNotes(requestDetails.notes ?? "");
    setDescription(requestDetails.description ?? "");
    setWarranties(requestToWarranties(requestDetails));
    setCompatibleWarrantyIds(
      ((requestDetails as ProductRequestDto & { compatibleWarrantyIds?: number[] }).compatibleWarrantyIds) ?? [],
    );

    const { options: hydratedOptions, variantEdits: hydratedVariantEdits } =
      requestToVariantBuilderState(requestDetails);
    setVariantsEnabled(hydratedOptions.length > 0);
    setOptions(hydratedOptions.length > 0 ? hydratedOptions : DEFAULT_OPTIONS);
    setVariantEdits(hydratedVariantEdits);
    setRemovedVariantKeys(new Set());
    setVisibility(true);

    setDraftHydrated(true);
    window.setTimeout(() => {
      suppressDraftPersistenceRef.current = false;
    }, 0);
  }, [draftHydrated, requestDetails, requestId]);

  // Hydrate from bulk preview DTO (passed via location.state from the bulk upload results page)
  useEffect(() => {
    if (!bulkPreview || hydratedRequestRef.current) return;

    hydratedRequestRef.current = true;
    suppressDraftPersistenceRef.current = true;

    setHasSavedDraft(false);
    const dto = bulkPreview as CreateProductRequestDto & { image?: string; images?: string[] };
    setImages([...(dto.image ? [dto.image] : []), ...(dto.images ?? [])]);
    setTemplateImageFiles([]);
    setName(dto.name ?? "");
    setBrandId(dto.brandId != null ? String(dto.brandId) : "");
    setCategoryId(dto.categoryId != null ? String(dto.categoryId) : "");
    setSku(dto.variants?.[0]?.sku ?? "");
    setCountryOfOriginId(dto.countryOfOriginId != null ? String(dto.countryOfOriginId) : "");
    setDealTypeId(dto.dealTypeId != null ? String(dto.dealTypeId) : "");
    setScreenSizeId(dto.screenSizeId != null ? String(dto.screenSizeId) : "");
    setResolutionId(dto.resolutionId != null ? String(dto.resolutionId) : "");
    setDatasheetUrl(dto.datasheetUrl ?? "");
    setDatasheetObjectKey(dto.datasheetObjectKey ?? null);
    setDatasheetFileName(dto.datasheetFileName ?? null);
    setDatasheetExtractionId(dto.datasheetExtractionId ?? null);
    setNotes((dto as any).notes ?? "");
    setDescription(dto.description ?? "");
    setWarranties([{
      id: 1,
      warrantyId: dto.warrantyId != null ? String(dto.warrantyId) : "",
      warrantyTypeId: dto.warrantyTypeId != null ? String(dto.warrantyTypeId) : "",
      warrantyProviderId: (dto as any).warrantyProviderId != null ? String((dto as any).warrantyProviderId) : "",
    }]);

    // Reuse the variant builder state logic — CreateProductRequestDto variants have the same shape
    const { options: hydratedOptions, variantEdits: hydratedVariantEdits } =
      requestToVariantBuilderState(dto as unknown as ProductRequestDto);
    setVariantsEnabled(hydratedOptions.length > 0);
    setOptions(hydratedOptions.length > 0 ? hydratedOptions : DEFAULT_OPTIONS);
    setVariantEdits(hydratedVariantEdits);
    setRemovedVariantKeys(new Set());
    setVisibility(true);

    setDraftHydrated(true);
    window.setTimeout(() => {
      suppressDraftPersistenceRef.current = false;
    }, 0);
  }, [bulkPreview, draftHydrated]);

  // Only an unsaved new product is autosaved locally: server drafts and bulk
  // previews would otherwise leak into the next "Add Product Manually".
  const persistsLocally = !requestId && !isBulkPreview && !savedRequestId;

  useEffect(() => {
    if (!draftHydrated || suppressDraftPersistenceRef.current || !persistsLocally) return;

    const timeout = window.setTimeout(() => {
      async function persistDraft() {
        try {
          const [serializedTemplateImages, serializedVariantImages] = await Promise.all([
            Promise.all(templateImageFiles.map((file) => serializeFile(file))),
            Promise.all(
              Object.entries(variantEdits).map(async ([key, edit]) => {
                if (!edit.imageFile) return [key, undefined] as const;
                const serialized = await serializeFile(edit.imageFile);
                return [key, serialized] as const;
              }),
            ),
          ]);

          const draft: MyProductDraft = {
            version: 2,
            images: serializedTemplateImages,
            name,
            brandId,
            categoryId,
            sku,
            countryOfOriginId,
            dealTypeId,
            screenSizeId,
            resolutionId,
            datasheetUrl,
            datasheetObjectKey,
            datasheetFileName,
            datasheetExtractionId,
            notes,
            description,
            warranties,
            variantsEnabled,
            options,
            variantEdits: Object.fromEntries(
              serializedVariantImages.map(([key, serialized]) => {
                const rest = omit(variantEdits[key], "imageFile", "imagePreviewUrl");
                return [
                  key,
                  serialized
                    ? { ...rest, imageFile: serialized, imagePreviewUrl: serialized.dataUrl }
                    : rest,
                ] as const;
              }),
            ),
            removedVariantKeys: Array.from(removedVariantKeys),
            visibility,
          };

          if (!isNonEmptyDraft(draft)) {
            localStorage.removeItem(draftStorageKey);
            setHasSavedDraft(false);
            return;
          }

          localStorage.setItem(draftStorageKey, JSON.stringify(draft));
          setHasSavedDraft(true);
        } catch {
          // Best effort only. If serialization fails or storage quota is hit,
          // we avoid breaking the form.
        }
      }

      void persistDraft();
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [
    draftHydrated,
    draftStorageKey,
    images,
    name,
    brandId,
    categoryId,
    sku,
    countryOfOriginId,
    dealTypeId,
    screenSizeId,
    resolutionId,
    datasheetUrl,
    datasheetObjectKey,
    datasheetFileName,
    datasheetExtractionId,
    notes,
    description,
    warranties,
    variantsEnabled,
    options,
    variantEdits,
    removedVariantKeys,
    visibility,
    templateImageFiles,
    persistsLocally,
  ]);

  const activeOptions = useMemo(
    () => options.filter((o) => o.name && o.valueIds.length > 0),
    [options],
  );
  const activeOptionsCount = activeOptions.length;

  const variants: ResolvedVariant[] = useMemo(() => {
    if (!variantsEnabled || activeOptions.length === 0) return [];
    type Cell = { axis: VariantOptionAxis; valueId: string; label: string };
    const matrix: Cell[][] = activeOptions.map((opt) =>
      opt.valueIds.map((vid) => ({
        axis: opt.name,
        valueId: vid,
        label: labelsByAxis[opt.name]?.[vid] ?? vid,
      })),
    );
    const combos = matrix.reduce<Cell[][]>(
      (acc, row) => acc.flatMap((combo) => row.map((cell) => [...combo, cell])),
      [[]],
    );
    return combos
      .map((combo): ResolvedVariant => {
        const key = combo.map((c) => `${c.axis}:${c.valueId}`).join("|");
        const label = combo.map((c) => c.label).join(" / ");
        const axisValues: Partial<Record<NonEmptyVariantAxis, number>> = {};
        for (const cell of combo) {
          if (!cell.axis) continue;
          const id = parseIntOrUndef(cell.valueId);
          if (id != null) axisValues[cell.axis as NonEmptyVariantAxis] = id;
        }
        return { key, label, axisValues };
      })
      .filter((v) => !removedVariantKeys.has(v.key));
  }, [variantsEnabled, activeOptions, labelsByAxis, removedVariantKeys]);

  const buildDto = (
    isDraft: boolean,
    objectKeysByVariantKey: Record<string, string>,
    templateImageObjectKeys: string[],
  ): CreateProductRequestDto => {
    const [templateImageObjectKey, ...galleryImageObjectKeys] = templateImageObjectKeys;
    const firstWarranty = warranties[0];
    const apiVariants: CreateProductVariantDto[] = variantsEnabled && variants.length > 0
      ? variants.map((v) => {
          const edit = variantEdits[v.key];
          const axisFields: Partial<CreateProductVariantDto> = {};
          for (const [axis, valueId] of Object.entries(v.axisValues)) {
            const field = AXIS_DTO_FIELD[axis as NonEmptyVariantAxis];
            (axisFields as Record<string, number>)[field] = valueId as number;
          }
          const variantPricingTiers = (edit?.pricingTiers ?? [])
            .map((t) => ({
              minQty: parseIntOrUndef(t.minQty),
              price: parseFloatOrUndef(t.price),
            }))
            .filter((t): t is { minQty: number; price: number } =>
              t.minQty != null && t.price != null,
            );
          return {
            ...axisFields,
            sku: edit?.sku || sku || undefined,
            quantity: parseIntOrUndef(edit?.stock ?? ""),
            image: objectKeysByVariantKey[v.key],
            conditionId: parseIntOrUndef(edit?.conditionId ?? ""),
            model: edit?.model?.trim() || undefined,
            pricingTiers: variantPricingTiers,
          };
        })
      : [
          // Single implicit variant when variants are disabled — carries the
          // top-level SKU so the seller has something to ship against.
          { sku: sku || undefined, pricingTiers: [] },
        ];

    return {
      name,
      brandId: parseIntOrUndef(brandId),
      categoryId: parseIntOrUndef(categoryId),
      countryOfOriginId: parseIntOrUndef(countryOfOriginId),
      // Drop values for fields the category hides, so stale data isn't persisted.
      dealTypeId: fieldPolicy.dealType.visible ? parseIntOrUndef(dealTypeId) : undefined,
      screenSizeId: fieldPolicy.screenSize.visible ? parseIntOrUndef(screenSizeId) : undefined,
      resolutionId: fieldPolicy.resolution.visible ? parseIntOrUndef(resolutionId) : undefined,
      datasheetUrl: fieldPolicy.datasheet.visible ? datasheetUrl || undefined : undefined,
      datasheetObjectKey: fieldPolicy.datasheet.visible ? datasheetObjectKey ?? undefined : undefined,
      datasheetFileName: fieldPolicy.datasheet.visible ? datasheetFileName ?? undefined : undefined,
      datasheetExtractionId: fieldPolicy.datasheet.visible ? datasheetExtractionId ?? undefined : undefined,
      description: description || undefined,
      warrantyId: fieldPolicy.warranty.visible ? parseIntOrUndef(firstWarranty?.warrantyId ?? "") : undefined,
      warrantyTypeId: fieldPolicy.warrantyType.visible ? parseIntOrUndef(firstWarranty?.warrantyTypeId ?? "") : undefined,
      warrantyProviderId: fieldPolicy.warrantyProvider.visible ? parseIntOrUndef(firstWarranty?.warrantyProviderId ?? "") : undefined,
      compatibleWarrantyIds: compatibleWarrantyIds.length > 0 ? compatibleWarrantyIds : undefined,
      notes: fieldPolicy.notes.visible ? notes.trim() || undefined : undefined,
      productType: "consu",
      variants: apiVariants,
      isDraft,
      // CreateProductRequestDto.image and .images are exposed by the backend
      // but won't appear in the generated client until `npm run generate:api`
      // runs after the BE schema/DTO change. The cast disappears after regen.
      ...(templateImageObjectKey
        ? { image: templateImageObjectKey }
        : images[0] && !images[0].startsWith("blob:") ? { image: images[0] } : {}),
      ...(galleryImageObjectKeys.length > 0
        ? { images: galleryImageObjectKeys }
        : images.length > 1
          ? { images: images.slice(1).filter((u) => !u.startsWith("blob:")) }
          : {}),
    } as unknown as CreateProductRequestDto;
  };

  function validateSubmitForm(): string[] {
    const errors: string[] = [];
    const errKeys = new Set<string>();

    function require(key: string, label: string, value: string) {
      if (!isNonEmptyString(value)) { errors.push(`${label} is required`); errKeys.add(key); }
    }

    // Core fields are always required regardless of category policy.
    require("name", "Product name", name);
    require("brandId", "Brand", brandId);
    require("categoryId", "Category", categoryId);
    require("sku", "SKU", sku);
    require("countryOfOriginId", "Country of origin", countryOfOriginId);
    require("description", "Product description", description);

    // Per-category spec fields: only required when the category's policy says so
    // (and the field is visible). This replaces the old "always required".
    const requireByPolicy = (
      key: "screenSize" | "resolution" | "dealType",
      errKey: string,
      label: string,
      value: string,
    ) => {
      const policy = fieldPolicy[key];
      if (policy.visible && policy.required) require(errKey, label, value);
    };
    requireByPolicy("dealType", "dealTypeId", "Deal type", dealTypeId);
    requireByPolicy("screenSize", "screenSizeId", "Screen size", screenSizeId);
    requireByPolicy("resolution", "resolutionId", "Resolution", resolutionId);

    if (images.length === 0) {
      errors.push("At least one product image is required");
      errKeys.add("images");
    }

    if (fieldPolicy.datasheet.visible && fieldPolicy.datasheet.required) {
      const hasDatasheet =
        isNonEmptyString(datasheetObjectKey ?? "") || isNonEmptyString(datasheetUrl);
      if (!hasDatasheet) errors.push("Product datasheet is required for this category");
    }

    // Warranty is gated by policy. When hidden, skip entirely; when visible,
    // each sub-field is required only if its policy marks it required.
    if (fieldPolicy.warranty.visible) {
      const reqWarranty = fieldPolicy.warranty.required;
      const reqType = fieldPolicy.warrantyType.visible && fieldPolicy.warrantyType.required;
      const reqProvider =
        fieldPolicy.warrantyProvider.visible && fieldPolicy.warrantyProvider.required;
      if (reqWarranty && warranties.length === 0) {
        errors.push("Included warranty is required");
      }
      warranties.forEach((w, index) => {
        if (reqWarranty && !isNonEmptyString(w.warrantyId)) { errors.push(`Warranty ${index + 1}: period is required`); errKeys.add(`warranty:${index}:warrantyId`); }
        if (reqType && !isNonEmptyString(w.warrantyTypeId)) { errors.push(`Warranty ${index + 1}: type is required`); errKeys.add(`warranty:${index}:warrantyTypeId`); }
        if (reqProvider && !isNonEmptyString(w.warrantyProviderId)) { errors.push(`Warranty ${index + 1}: provider is required`); errKeys.add(`warranty:${index}:warrantyProviderId`); }
      });
    }

    if (variantsEnabled) {
      if (activeOptionsCount === 0) {
        errors.push("Add at least one variant option");
      }

      // Variant axes required by the category policy must have at least one
      // configured option. Axis policy keys map to the builder's option names.
      const AXIS_LABEL: Record<"ram" | "cpu" | "storage" | "color", string> = {
        ram: "RAM",
        cpu: "CPU",
        storage: "Storage",
        color: "Color",
      };
      (["ram", "cpu", "storage", "color"] as const).forEach((axisKey) => {
        const policy = fieldPolicy[axisKey];
        if (!policy.visible || !policy.required) return;
        const label = AXIS_LABEL[axisKey];
        const present = activeOptions.some(
          (o) => o.name.trim().toLowerCase() === label.toLowerCase() && o.valueIds.length > 0,
        );
        if (!present) errors.push(`${label} is required for this category — add it as a variant option`);
      });

      activeOptions.forEach((option, index) => {
        if (!isNonEmptyString(option.name)) {
          errors.push(`Variant option ${index + 1}: name is required`);
        }
        if (option.valueIds.length === 0) {
          errors.push(`Variant option ${index + 1}: at least one value is required`);
        }
      });

      variants.forEach((variant) => {
        const edit = variantEdits[variant.key];
        if (!edit) {
          errors.push(`Variant ${variant.label}: missing row data`);
          return;
        }

        if (!isNonEmptyString(edit.sku)) { errors.push(`Variant ${variant.label}: SKU is required`); errKeys.add(`variant:${variant.key}:sku`); }
        if (!isNonEmptyString(edit.stock)) { errors.push(`Variant ${variant.label}: stock is required`); errKeys.add(`variant:${variant.key}:stock`); }
        if (fieldPolicy.condition.visible && fieldPolicy.condition.required && !isNonEmptyString(edit.conditionId)) {
          errors.push(`Variant ${variant.label}: condition is required`);
          errKeys.add(`variant:${variant.key}:conditionId`);
        }
        if (!edit.pricingTiers || edit.pricingTiers.length === 0) {
          errors.push(`Variant ${variant.label}: at least one pricing tier is required`);
        }
        edit.pricingTiers?.forEach((tier, ti) => {
          const minQtyError = validateNumberField(`Variant ${variant.label} tier ${ti + 1} min qty`, tier.minQty);
          const priceError = validateNumberField(`Variant ${variant.label} tier ${ti + 1} price`, tier.price);
          if (minQtyError) { errors.push(minQtyError); errKeys.add(`variant:${variant.key}:tier:${tier.id}:minQty`); }
          if (priceError) { errors.push(priceError); errKeys.add(`variant:${variant.key}:tier:${tier.id}:price`); }
        });
      });
    }

    setFieldErrors(errKeys);
    return errors;
  }

  /**
   * Uploads every staged template-level image in parallel. Resolves to an
   * ordered array of GCS objectKeys (matches `templateImageFiles` order).
   * The first key becomes the main `image` (product.template.image_1920); the
   * rest become gallery rows on `product.image`. Rejects on the first failure
   * so the caller can abort the request.
   */
  async function uploadTemplateImages(): Promise<string[]> {
    if (templateImageFiles.length === 0) return [];
    setTemplateUploadProgress(0);
    const total = templateImageFiles.length;
    const completed = { count: 0 };
    return Promise.all(
      templateImageFiles.map((file) =>
        uploadProductImage(file, {
          onProgress: (percent) => {
            // Aggregate progress across files. Cheap heuristic: average the
            // current file's percent across all queued files.
            const overall = Math.round(
              (completed.count * 100 + percent) / total,
            );
            setTemplateUploadProgress(overall);
          },
        }).then((key) => {
          completed.count += 1;
          return key;
        }),
      ),
    );
  }

  /**
   * Uploads every queued variant image in parallel. Resolves to a map of
   * `{ variantKey: objectKey }`. Rejects on the first failure so the caller
   * can abort the request and surface a single error.
   */
  async function uploadVariantImages(): Promise<Record<string, string>> {
    if (!variantsEnabled) return {};
    const queued = variants
      .map((v) => ({ key: v.key, file: variantEdits[v.key]?.imageFile }))
      .filter((x): x is { key: string; file: File } => Boolean(x.file));

    if (queued.length === 0) return {};

    setUploadProgress(Object.fromEntries(queued.map((q) => [q.key, 0])));

    const results = await Promise.all(
      queued.map(async ({ key, file }) => {
        const objectKey = await uploadProductImage(file, {
          onProgress: (percent) =>
            setUploadProgress((prev) => ({ ...prev, [key]: percent })),
        });
        return [key, objectKey] as const;
      }),
    );

    return Object.fromEntries(results);
  }

  async function submit(isDraft: boolean) {
    if (!isDraft && !canSubmitProductRequest) {
      showErrorToast(
        productRequestBlockReason || "You cannot submit a product request yet.",
      );
      return;
    }
    if (!isDraft && !name.trim()) {
      showErrorToast("Product name is required");
      return;
    }
    if (!isDraft) {
      const validationErrors = validateSubmitForm();
      if (validationErrors.length > 0) {
        showErrorToast(validationErrors.slice(0, 3).join(". "));
        return;
      }
    } else {
      setFieldErrors(new Set());
    }
    setSubmitting(true);
    try {
      let objectKeysByVariantKey: Record<string, string> = {};
      let templateImageObjectKeys: string[] = [];
      try {
        // Run template + variant uploads in parallel — if any one fails the
        // request is aborted (no partial submissions).
        [templateImageObjectKeys, objectKeysByVariantKey] = await Promise.all([
          uploadTemplateImages(),
          uploadVariantImages(),
        ]);
      } catch (err) {
        showErrorToast(
          err instanceof ProductImageUploadError
            ? `${err.message}. Product request was not submitted.`
            : "Image upload failed. Product request was not submitted.",
        );
        return;
      }

      const dto = buildDto(isDraft, objectKeysByVariantKey, templateImageObjectKeys);
      const targetRequestId = requestId ?? savedRequestId;
      const response = targetRequestId
        ? await updateProductRequest(targetRequestId, dto)
        : await productRequestsService.createProductRequest(dto);

      const nextRequestId = response.data.id;
      if (nextRequestId && nextRequestId !== savedRequestId) {
        setSavedRequestId(nextRequestId);
      }

      if (isBulkPreview) {
        // Update the bulk results in sessionStorage so the results page
        // reflects this product as confirmed (with an ID) or submitted.
        const CONFIRMED_KEY = "bulk-upload-confirmed";
        const PREVIEW_KEY = "bulk-upload-results";
        const confirmedRaw = sessionStorage.getItem(CONFIRMED_KEY);
        const previewRaw = sessionStorage.getItem(PREVIEW_KEY);

        if (confirmedRaw) {
          // Already have confirmed state — add this product
          const confirmed = JSON.parse(confirmedRaw);
          confirmed.requests = confirmed.requests ?? [];
          confirmed.requests.push(response.data);
          if (!isDraft) {
            confirmed.submittedIds = confirmed.submittedIds ?? [];
            confirmed.submittedIds.push(nextRequestId);
          }
          sessionStorage.setItem(CONFIRMED_KEY, JSON.stringify(confirmed));
        } else {
          // First product being saved — create confirmed state from preview
          const previews = previewRaw ? JSON.parse(previewRaw) : { previews: [], errors: [] };
          const remaining = [...(previews.previews ?? [])];
          if (bulkPreviewIndex != null) remaining.splice(bulkPreviewIndex, 1);
          sessionStorage.setItem(PREVIEW_KEY, JSON.stringify({ ...previews, previews: remaining }));
          sessionStorage.setItem(CONFIRMED_KEY, JSON.stringify({
            requests: [response.data],
            submittedIds: isDraft ? [] : [nextRequestId],
            errors: previews.errors ?? [],
          }));
        }

        suppressDraftPersistenceRef.current = true;
        localStorage.removeItem(draftStorageKey);
        showSuccessToast(isDraft ? "Draft saved" : "Submitted for review");
        navigate(AppRoutes.MY_PRODUCT_BULK_UPLOAD_RESULTS);
      } else if (isDraft) {
        showSuccessToast(targetRequestId ? "Draft updated" : "Draft saved");
        setHasSavedDraft(true);
        if (!requestId && nextRequestId) {
          // The draft now lives on the server: drop the local autosave so the
          // next "Add Product Manually" starts blank, and keep editing this
          // draft from its own URL (survives a reload, no duplicate on re-save).
          localStorage.removeItem(draftStorageKey);
          navigate(AppRoutes.MY_PRODUCT_REQUEST_EDIT.replace(":id", nextRequestId), {
            replace: true,
          });
        }
      } else {
        suppressDraftPersistenceRef.current = true;
        localStorage.removeItem(draftStorageKey);
        setHasSavedDraft(false);
        setSavedRequestId(undefined);
        showSuccessToast("Submitted for review");
        if (fromBulkUpload && nextRequestId) {
          const CONFIRMED_KEY = "bulk-upload-confirmed";
          const confirmedRaw = sessionStorage.getItem(CONFIRMED_KEY);
          if (confirmedRaw) {
            const confirmed = JSON.parse(confirmedRaw);
            confirmed.submittedIds = confirmed.submittedIds ?? [];
            if (!confirmed.submittedIds.includes(nextRequestId)) {
              confirmed.submittedIds.push(nextRequestId);
            }
            sessionStorage.setItem(CONFIRMED_KEY, JSON.stringify(confirmed));
          }
          navigate(AppRoutes.MY_PRODUCT_BULK_UPLOAD_RESULTS);
        } else {
          navigate(AppRoutes.MY_PRODUCT_REQUESTS);
        }
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      const flat = Array.isArray(msg) ? msg.join(", ") : msg;
      showErrorToast(flat || "Failed to save product request");
    } finally {
      setSubmitting(false);
      setUploadProgress({});
      setTemplateUploadProgress(undefined);
    }
  }

  async function discardDraft() {
    const targetId = requestId ?? savedRequestId;
    if (targetId) {
      try {
        await deleteProductRequest(targetId);
        showSuccessToast("Draft deleted");
      } catch {
        showErrorToast("Failed to delete draft");
        return;
      }
    }
    suppressDraftPersistenceRef.current = true;
    localStorage.removeItem(draftStorageKey);
    navigate(AppRoutes.MY_PRODUCT_REQUESTS);
  }

  const effectiveRequestId = requestId ?? savedRequestId;
  const isEditingDraft = Boolean(requestId || savedRequestId);
  const backRoute = isBulkPreview || fromBulkUpload
    ? AppRoutes.MY_PRODUCT_BULK_UPLOAD_RESULTS
    : requestId
      ? AppRoutes.PRODUCT_REQUEST_DETAILS.replace(":id", requestId)
      : AppRoutes.MY_PRODUCTS;
  const backLabel = isBulkPreview || fromBulkUpload
    ? "Back to Import"
    : requestId ? "Back to Draft" : "Back to Products";
  const pageTitle = isBulkPreview
    ? "Preview product"
    : isEditingDraft ? "Edit draft" : "Add a new product";
  const draftButtonLabel = effectiveRequestId ? "Update Draft" : "Save as Draft";

  function leavePage() {
    // Cancel / Back abandon an unsaved new product — its autosave must not
    // resurface the next time the seller adds a product.
    if (persistsLocally) {
      suppressDraftPersistenceRef.current = true;
      localStorage.removeItem(draftStorageKey);
    }
    navigate(backRoute);
  }

  if (requestId && requestDetailsError && !draftHydrated) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight">
        <div className="flex flex-1 flex-col gap-6 px-8 pt-8 pb-6 max-md:px-4">
          <SectionCard shadow title="Error" className="gap-2">
            <Text variant="body" className="text-accentError">
              Failed to load draft. Please try again later.
            </Text>
          </SectionCard>
        </div>
      </div>
    );
  }

  if (!draftHydrated) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight">
        <div className="flex flex-1 flex-col gap-6 px-8 pt-8 pb-6 max-md:px-4">
          <ShimmerLoader message="Loading draft…" lines={6} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full w-full flex-col bg-bgLight">
      <div className="flex flex-1 flex-col gap-4 px-8 pt-6 pb-6 max-md:px-4">
        <div className="flex items-center gap-2 max-md:flex-wrap">
          <Button variant="ghostAccent" size="sm" leftIcon={<IconArrowLeft size={18} />}
            onClick={leavePage} className="!h-auto !px-0 !py-0">
            <Heading variant="h3" tone="inherit">{backLabel}</Heading>
          </Button>
          <Text variant="bodySm" tone="muted" className="max-md:hidden">/</Text>
          <Heading variant="h3" tone="dark" className="max-md:basis-full">{pageTitle}</Heading>
          {isDebug && (
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={handleDevAutofill}
              className="ml-auto"
            >
              DEV - Autofill Product
            </Button>
          )}
        </div>

        {fieldPolicy.datasheet.visible && (
          <SectionCard
            shadow
            title={<>Product datasheet{fieldPolicy.datasheet.required && <RequiredMark />}</>}
            subtitle="Upload the manufacturer PDF — we'll read it and offer to autofill product details so you don't retype them."
            className="gap-5"
          >
            <DatasheetUpload
              objectKey={datasheetObjectKey}
              fileName={datasheetFileName}
              onUploaded={handleDatasheetUploaded}
              onRemove={handleDatasheetRemove}
              scope={savedRequestId ?? requestId}
            />
            {extracting ? (
              <ShimmerLoader message="AI is reading your datasheet — this takes 5–30 s for typical spec sheets…" />
            ) : null}
          </SectionCard>
        )}

        <SectionCard
          shadow
          title={<>General Product images<RequiredMark /></>}
          subtitle="These images represent your product and will be shown for every variant by default. You can override them for a specific variant later in Variants & pricing."
          className="gap-5"
        >
          <ImageDropzone
            images={images}
            onImagesChange={(next) => {
              setImages(next);
              // Keep the file list aligned with the preview list: when the
              // user removes a preview, drop the corresponding queued file.
              setTemplateImageFiles((prev) => prev.slice(0, next.length));
              if (next.length > 0 && fieldErrors.has("images")) {
                setFieldErrors((prev) => {
                  const nextErrors = new Set(prev);
                  nextErrors.delete("images");
                  return nextErrors;
                });
              }
            }}
            onFilesAdded={(files) => {
              // The first file becomes product.template.image_1920 (main).
              // The rest become product.image rows on the template's gallery.
              setTemplateImageFiles((prev) => [...prev, ...files]);
            }}
            uploadProgress={templateUploadProgress}
          />
          {fieldErrors.has("images") && (
            <Text variant="bodySm" className="text-accentError">
              At least one product image is required.
            </Text>
          )}
        </SectionCard>

        <BasicInfoSection
          name={name}                     onNameChange={setName}
          brandId={brandId}               onBrandIdChange={setBrandId}
          categoryId={categoryId}         onCategoryIdChange={setCategoryId}
          sku={sku}                       onSkuChange={setSku}
          countryOfOriginId={countryOfOriginId}
          onCountryOfOriginIdChange={setCountryOfOriginId}
          dealTypeId={dealTypeId}          onDealTypeIdChange={setDealTypeId}
          screenSizeId={screenSizeId}      onScreenSizeIdChange={setScreenSizeId}
          resolutionId={resolutionId}      onResolutionIdChange={setResolutionId}
          notes={notes}                    onNotesChange={setNotes}
          description={description}       onDescriptionChange={setDescription}
          fieldPolicy={fieldPolicy}
          fieldErrors={fieldErrors}
        />

        {fieldPolicy.warranty.visible && (
          <SectionCard
            shadow
            title={<>Included warranty{fieldPolicy.warranty.required && <RequiredMark />}</>}
            subtitle="Coverage that ships with every unit. For paid add-ons buyers can choose at checkout, see Extended warranties below."
            className="gap-5"
          >
            <div className="flex flex-col gap-4">
              {warranties.map((w, i) => (
                <WarrantyCard
                  key={w.id}
                  warranty={w}
                  index={i}
                  required={warrantyRequirements}
                  onRemove={() => setWarranties((prev) => prev.filter((x) => x.id !== w.id))}
                  onUpdate={(patch) => setWarranties((prev) => prev.map((x) => x.id === w.id ? { ...x, ...patch } : x))}
                />
              ))}
              <Button
                variant="secondaryDark"
                size="sm"
                leftIcon={<IconPlus size={14} />}
                className="self-start"
                onClick={() =>
                  setWarranties((prev) => [
                    ...prev,
                    { id: Date.now(), warrantyId: "", warrantyTypeId: "", warrantyProviderId: "" },
                  ])
                }
              >
                Add warranty option
              </Button>
            </div>
          </SectionCard>
        )}

        <CompatibleWarrantiesSection
          selectedIds={compatibleWarrantyIds}
          onChange={setCompatibleWarrantyIds}
        />

        <VariantsBuilderSection
          enabled={variantsEnabled}
          onEnabledChange={setVariantsEnabled}
          options={options}
          onOptionRemove={(id) => setOptions((prev) => prev.filter((o) => o.id !== id))}
          onOptionUpdate={(id, patch) =>
            setOptions((prev) => prev.map((o) => (o.id === id ? { ...o, ...patch } : o)))
          }
          onOptionAdd={() =>
            setOptions((prev) => [...prev, { id: Date.now(), name: "", valueIds: [] }])
          }
          onLabelsResolved={handleLabelsResolved}
          variants={variants}
          variantEdits={variantEdits}
          onVariantEditUpdate={(key, patch) =>
            setVariantEdits((prev) => ({ ...prev, [key]: { ...(prev[key] ?? { sku: "", stock: "", pricingTiers: [{ id: 1, minQty: "1", price: "" }] }), ...patch } }))
          }
          onVariantRemove={(key) => setRemovedVariantKeys((prev) => new Set([...prev, key]))}
          activeOptionsCount={activeOptionsCount}
          uploadProgress={uploadProgress}
          fieldErrors={fieldErrors}
          conditionRequired={fieldPolicy.condition.visible && fieldPolicy.condition.required}
        />

        <SectionCard shadow title="Visibility" className="gap-5">
          <div className="flex items-center gap-3">
            <Switch checked={visibility} onChange={setVisibility} aria-label="List product in catalog" />
            <Text variant="body" tone="dark">List product in catalog immediately</Text>
          </div>
        </SectionCard>

      </div>

      <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-borderDefault bg-bgWhite px-8 py-4 max-md:flex-wrap max-md:gap-2 max-md:px-4 max-md:py-3 max-md:[&>button]:grow max-md:[&>button]:basis-[calc(50%-4px)] max-md:[&>button]:!px-3">
        {!canSubmitProductRequest && (
          <div className="mr-auto max-w-xl rounded-xl border border-borderDefault bg-bgLight px-4 py-3 max-md:mr-0 max-md:w-full">
            <Text variant="bodySm" tone="dark" className="font-medium">
              Product requests are locked until your documents are approved.
            </Text>
            <Text variant="bodySm" tone="body" className="mt-1">
              {productRequestBlockReason}
            </Text>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => navigate(isSeller ? AppRoutes.SETTINGS : AppRoutes.CHANGE_REQUEST_NEW_BUYER_PROFILE)}
            >
              {isSeller ? "Open seller settings" : "Open profile change request"}
            </Button>
          </div>
        )}
        {hasSavedDraft && !isBulkPreview && (
          <Button variant="ghost" size="md" onClick={discardDraft} disabled={submitting}>
            Discard draft
          </Button>
        )}
        <Button variant="secondary" size="md" onClick={leavePage} disabled={submitting}>
          {isBulkPreview ? "Back to Import" : "Cancel"}
        </Button>
        <Button
          variant="secondary"
          size="md"
          className="!px-8 !bg-bgMuted !border-bgMuted hover:!border-borderDefault !text-textDark"
          onClick={() => submit(true)}
          disabled={submitting}
        >
          {submitting
            ? Object.keys(uploadProgress).length > 0 || templateUploadProgress != null
              ? "Saving changes…"
              : "Saving…"
            : draftButtonLabel}
        </Button>
        <Button
          variant="primary"
          size="md"
          className="!px-8"
          onClick={() => submit(false)}
          disabled={submitting || !canSubmitProductRequest || images.length === 0}
        >
          {submitting
            ? Object.keys(uploadProgress).length > 0 || templateUploadProgress != null
              ? "Saving changes…"
              : "Submitting…"
            : "Submit for Review"}
        </Button>
      </div>

      <DatasheetExtractionDialog
        open={extractionDialogOpen}
        extraction={parsedDatasheetSummary}
        brandOptions={brandOptionsForDialog}
        categoryOptions={categoryOptionsForDialog}
        onApply={applyDatasheetAutofill}
        onSkip={() => setExtractionDialogOpen(false)}
      />
    </div>
  );
}
